import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw, Trophy, Clock, Star } from "lucide-react";
import { Button } from "../components/ui/button";

const DREAM_SYMBOLS = [
  { id: "moon", emoji: "\u{1F319}", label: "Kuu" },
  { id: "star", emoji: "\u{2B50}", label: "Tähti" },
  { id: "cloud", emoji: "\u{2601}\u{FE0F}", label: "Pilvi" },
  { id: "water", emoji: "\u{1F30A}", label: "Vesi" },
  { id: "fire", emoji: "\u{1F525}", label: "Tuli" },
  { id: "eye", emoji: "\u{1F441}\u{FE0F}", label: "Silmä" },
  { id: "key", emoji: "\u{1F511}", label: "Avain" },
  { id: "butterfly", emoji: "\u{1F98B}", label: "Perhonen" },
  { id: "mountain", emoji: "\u{26F0}\u{FE0F}", label: "Vuori" },
  { id: "mirror", emoji: "\u{1FA9E}", label: "Peili" },
  { id: "feather", emoji: "\u{1FAB6}", label: "Sulka" },
  { id: "crystal", emoji: "\u{1F48E}", label: "Kristalli" },
  { id: "door", emoji: "\u{1F6AA}", label: "Ovi" },
  { id: "tree", emoji: "\u{1F333}", label: "Puu" },
  { id: "bird", emoji: "\u{1F54A}\u{FE0F}", label: "Lintu" },
  { id: "snake", emoji: "\u{1F40D}", label: "Käärme" },
];

const DIFFICULTIES = [
  { key: "easy", pairs: 6, label: "Helppo", cols: 4 },
  { key: "medium", pairs: 8, label: "Keskitaso", cols: 4 },
  { key: "hard", pairs: 12, label: "Vaikea", cols: 6 },
];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function MemoryGame() {
  const { t } = useTranslation();
  const [difficulty, setDifficulty] = useState(null);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [bestScores, setBestScores] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("uneksia_memory_scores") || "{}");
    } catch { return {}; }
  });
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    let interval;
    if (running) {
      interval = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [running]);

  const startGame = useCallback((diff) => {
    const symbols = shuffleArray(DREAM_SYMBOLS).slice(0, diff.pairs);
    const pairs = shuffleArray([...symbols, ...symbols].map((s, i) => ({
      ...s,
      uid: `${s.id}-${i}`,
    })));
    setCards(pairs);
    setDifficulty(diff);
    setFlipped([]);
    setMatched(new Set());
    setMoves(0);
    setTime(0);
    setRunning(true);
    setShowComplete(false);
  }, []);

  const handleFlip = useCallback((uid) => {
    if (flipped.length === 2) return;
    if (matched.has(uid) || flipped.includes(uid)) return;

    const newFlipped = [...flipped, uid];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      const card1 = cards.find(c => c.uid === first);
      const card2 = cards.find(c => c.uid === second);

      if (card1.id === card2.id) {
        const newMatched = new Set(matched);
        newMatched.add(first);
        newMatched.add(second);
        setMatched(newMatched);
        setFlipped([]);

        if (newMatched.size === cards.length) {
          setRunning(false);
          setTimeout(() => {
            setShowComplete(true);
            const key = difficulty.key;
            const prev = bestScores[key];
            if (!prev || moves + 1 < prev.moves) {
              const newScores = { ...bestScores, [key]: { moves: moves + 1, time } };
              setBestScores(newScores);
              localStorage.setItem("uneksia_memory_scores", JSON.stringify(newScores));
            }
          }, 500);
        }
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  }, [flipped, matched, cards, moves, time, difficulty, bestScores]);

  const getStars = () => {
    if (!difficulty) return 0;
    const optimal = difficulty.pairs;
    const ratio = moves / optimal;
    if (ratio <= 1.5) return 3;
    if (ratio <= 2.5) return 2;
    return 1;
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!difficulty) {
    return (
      <div data-testid="memory-game-menu" className="text-center space-y-8">
        <div>
          <h3 className="text-2xl font-heading font-light text-void mb-2">
            {t("games.memoryTitle") || "Unisymbolien muistipeli"}
          </h3>
          <p className="text-void/60 font-body text-sm max-w-md mx-auto">
            {t("games.memoryDesc") || "Etsi unisymbolien parit ja testaa muistiasi. Mitä vähemmän siirtoja, sitä parempi!"}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto">
          {DIFFICULTIES.map((diff) => (
            <button
              key={diff.key}
              data-testid={`memory-difficulty-${diff.key}`}
              onClick={() => startGame(diff)}
              className="group relative p-6 rounded-2xl border border-purple-200/50 bg-white/60 backdrop-blur-sm hover:bg-purple-50/80 hover:border-purple-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className="text-3xl mb-2">{diff.pairs === 6 ? "\u{1F319}" : diff.pairs === 8 ? "\u{2B50}" : "\u{1F48E}"}</div>
              <div className="font-heading text-void font-medium">{diff.label}</div>
              <div className="text-xs text-void/50 font-body mt-1">{diff.pairs} {t("games.pairs") || "paria"}</div>
              {bestScores[diff.key] && (
                <div className="mt-3 pt-2 border-t border-purple-100">
                  <div className="flex items-center justify-center gap-1 text-xs text-purple-600">
                    <Trophy className="w-3 h-3" />
                    <span>{bestScores[diff.key].moves} {t("games.moves") || "siirtoa"}</span>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (showComplete) {
    const stars = getStars();
    return (
      <div data-testid="memory-game-complete" className="text-center space-y-6 py-8">
        <div className="text-6xl animate-bounce">{stars === 3 ? "\u{1F31F}" : stars === 2 ? "\u{2B50}" : "\u{1F319}"}</div>
        <h3 className="text-2xl font-heading font-light text-void">
          {t("games.complete") || "Onneksi olkoon!"}
        </h3>
        <div className="flex justify-center gap-1">
          {[1, 2, 3].map(s => (
            <Star key={s} className={`w-8 h-8 ${s <= stars ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
          ))}
        </div>
        <div className="flex justify-center gap-8 text-void/70 font-body">
          <div className="text-center">
            <div className="text-2xl font-medium text-void">{moves}</div>
            <div className="text-xs">{t("games.moves") || "siirtoa"}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-medium text-void">{formatTime(time)}</div>
            <div className="text-xs">{t("games.time") || "aika"}</div>
          </div>
        </div>
        <div className="flex justify-center gap-3">
          <Button
            data-testid="memory-play-again"
            onClick={() => startGame(difficulty)}
            className="rounded-full bg-void text-white hover:bg-void/90"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t("games.playAgain") || "Pelaa uudestaan"}
          </Button>
          <Button
            data-testid="memory-change-difficulty"
            variant="outline"
            onClick={() => setDifficulty(null)}
            className="rounded-full"
          >
            {t("games.changeDifficulty") || "Vaihda vaikeustaso"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="memory-game-board">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-sm font-body text-void/70">
          <span className="flex items-center gap-1">
            <RotateCcw className="w-3.5 h-3.5" /> {moves} {t("games.moves") || "siirtoa"}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {formatTime(time)}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDifficulty(null)}
          className="text-void/50 hover:text-void text-xs"
        >
          {t("games.back") || "Takaisin"}
        </Button>
      </div>

      <div
        className="grid gap-2 sm:gap-3 mx-auto"
        style={{
          gridTemplateColumns: `repeat(${difficulty.cols}, minmax(0, 1fr))`,
          maxWidth: difficulty.cols === 6 ? "480px" : "380px",
        }}
      >
        {cards.map((card) => {
          const isFlipped = flipped.includes(card.uid) || matched.has(card.uid);
          const isMatched = matched.has(card.uid);
          return (
            <button
              key={card.uid}
              data-testid={`memory-card-${card.uid}`}
              onClick={() => handleFlip(card.uid)}
              disabled={isMatched}
              className={`aspect-square rounded-xl text-2xl sm:text-3xl transition-all duration-300 border ${
                isMatched
                  ? "bg-purple-100 border-purple-300 scale-95 opacity-70"
                  : isFlipped
                  ? "bg-white border-purple-300 shadow-lg scale-105"
                  : "bg-gradient-to-br from-indigo-900 to-purple-900 border-purple-700/50 hover:from-indigo-800 hover:to-purple-800 hover:shadow-md cursor-pointer"
              }`}
              style={{ perspective: "600px" }}
            >
              {isFlipped ? (
                <span className={`block transition-transform duration-200 ${isMatched ? "animate-pulse" : ""}`}>
                  {card.emoji}
                </span>
              ) : (
                <span className="text-purple-300/40 text-lg">{"\u{2728}"}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
